import { ReactNode } from 'react';
import Badge from '@v2/shared/components/Badge';
import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import ButtonLink from '@v2/shared/components/ButtonLink';
import ContentCard from '@v2/shared/components/ContentCard';
import PageHeading from '@v2/shared/components/PageHeading';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';

/**
 * The page's sections, in order, as anchor id to the name both navigations call it.
 *
 * One list rather than three: the at-a-glance strip, the jump pills and the cards below
 * are all keyed to it, and `SectionId` is what stops a pill pointing at an anchor no
 * section carries — the failure that is invisible until someone clicks.
 */
const SECTIONS = {
  print: 'Print subscriptions',
  digital: 'Digital magazines',
  bundles: 'Print + Digital bundles',
  books: 'Books & goods',
  where: 'Where we ship',
  help: 'Something not right?',
} as const;

type SectionId = keyof typeof SECTIONS;

/** The four headline answers, above the sections that give them in full. */
const GLANCE: { id: SectionId; term: string; answer: string; note: string }[] = [
  {
    id: 'print',
    term: 'Print subscriptions',
    answer: '4 to 12 weeks',
    note: 'Free shipping, always',
  },
  { id: 'digital', term: 'Digital', answer: 'Within 60 minutes', note: 'Nothing ships' },
  {
    id: 'bundles',
    term: 'Print + Digital',
    answer: 'Digital first',
    note: 'Print follows in 4 to 12 weeks',
  },
  {
    id: 'books',
    term: 'Books & goods',
    answer: '3 to 10 days',
    note: 'Shipping calculated at checkout',
  },
];

/**
 * Clears the fixed header, so a jump link does not land with the section's heading behind
 * it. The header is three heights and the tallest is in the middle: 53px below md, 126px
 * from md to xl where the search bar sits on its own row, and 82px at xl once it moves up
 * beside the nav. Same triple the Magzter Gold pricing panel measured.
 */
const SCROLL_OFFSET = 'scroll-mt-14 md:scroll-mt-32 xl:scroll-mt-24';

/** The chart cells, as /refund draws them. */
const CELL = 'border border-solid border-slate-200 px-3.5 py-2.5 text-left align-top';
const HEADER_CELL = `${CELL} bg-slate-50 font-bold text-slate-900`;

/** A boxed section, carrying the anchor both navigations above jump to. */
function Section({ id, children }: { id: SectionId; children: ReactNode }) {
  return (
    <ContentCard id={id} className={SCROLL_OFFSET}>
      {children}
    </ContentCard>
  );
}

/**
 * An aside inside a section — the "waiting longer than you should?" nudges.
 *
 * Deliberately not `Alert`: that component is page state (a form error, a confirmation),
 * and this is copy. It only needs the tint and the inset.
 */
function Note({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-solid border-slate-200 bg-slate-50 px-4 py-3.5 text-sm [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
      {children}
    </div>
  );
}

/** The data tables, which scroll rather than squeeze. See /refund's chart. */
function Chart({ children }: { children: ReactNode }) {
  return (
    <div className="not-prose my-4 w-full overflow-x-auto" tabIndex={0}>
      <table className="w-full min-w-[520px] border-collapse text-left text-sm text-slate-700">
        {children}
      </table>
    </div>
  );
}

/**
 * /shipping.
 *
 * The 2026-09-11 rebuild, against the v3 mockup on the task. The page it replaces spoke
 * only about print magazines and gave one 8-12 week window for all of them; it said
 * nothing about digital, bundles, books, military addresses or correctional facilities,
 * which is most of what customer service is asked. Every product we sell now has a
 * section, and the four headline answers are above them so the common question is
 * answered without scrolling.
 *
 * Copy is the content owner's and came with the mockup: it may not be re-worded,
 * re-ordered or re-punctuated without a task that supplies the new wording. What the
 * mockup left as `href="#"` is wired to the page that owns the subject, so this page
 * summarises and never restates — the Refund Policy owns returns, and DiscountLock owns
 * renewals.
 *
 * The delivery windows here are the mockup's and are written into the copy. They are
 * deliberately NOT the site-editable `otherpages.estimate` option that /guarantee reads:
 * that is one number for every title, and the whole point of the chart below is that the
 * wait depends on how often the magazine publishes.
 */
export default function ShippingDeliveryEstimates() {
  const { buildUrl } = useUrlGenerator();
  const contact = buildUrl('/contact');

  return (
    <>
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Shipping & Delivery' }]} />

      {/* The band carries its own deeper floor so the strip below can sit over its
          bottom edge, the way the rating card does on /reviews. `md:pb-16` is not a
          duplicate of `pb-16`: PageHeading's own floor is `md:py-6`, and Tailwind emits
          every responsive variant after the whole base layer, so a base `pb-16` loses to
          it from md up and the lede ends up behind the strip. Overriding a `md:` padding
          takes a `md:` padding. */}
      <PageHeading
        eyebrow="Shipping & Delivery"
        className="pb-16 md:pb-16"
        intro="Print subscriptions ship free and take a few weeks to start. Digital access is nearly instant. Books and other goods ship in days. Here is what to expect for everything we sell."
      >
        When your order arrives
      </PageHeading>

      {/* `relative` is load-bearing, not decoration: PageHeading is a positioned element,
          so a static strip pulled up over it is painted behind it and every card loses
          its label. Positioning this one puts it back on top. */}
      <nav aria-label="At a glance" className="relative -mt-10 px-3 md:px-5">
        <ul className="m-0 grid list-none grid-cols-2 gap-3 p-0 md:grid-cols-4">
          {GLANCE.map((entry) => (
            <li key={entry.id}>
              <a
                href={`#${entry.id}`}
                className="flex h-full flex-col rounded-lg border border-solid border-slate-200 bg-white p-4 no-underline shadow-sm hover:border-primary"
              >
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  {entry.term}
                </span>
                <span className="mt-1.5 text-base font-bold leading-tight text-slate-900">
                  {entry.answer}
                </span>
                <span className="mt-1 text-xs text-slate-500">{entry.note}</span>
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <nav aria-label="Jump to section" className="mt-7 flex flex-wrap gap-2">
        {Object.entries(SECTIONS).map(([id, label]) => (
          <a
            key={id}
            href={`#${id}`}
            className="rounded-full border border-solid border-slate-200 bg-white px-3.5 py-1.5 text-sm font-semibold text-slate-900 no-underline hover:border-primary hover:text-primary"
          >
            {label}
          </a>
        ))}
      </nav>

      <div className="prose mt-5 flex flex-col gap-6">
        <Section id="print">
          <h2>
            Print magazine subscriptions{' '}
            <Badge pill padding="snug" className="align-middle text-xs">
              Free shipping
            </Badge>
          </h2>

          <h3>Shipping is free</h3>
          <p>
            Shipping on every print magazine subscription is free. There are no handling fees, and
            the price you see is the price you pay for the whole term.
          </p>

          <h3>When your first issue arrives</h3>
          <p>
            Most first issues arrive within 4 to 12 weeks of ordering, depending on how often the
            magazine publishes. This is the industry-standard time publishers need to add you to
            their active mailing cycle, and it applies to every retailer, not just us.
          </p>

          <Chart>
            <thead>
              <tr>
                <th className={HEADER_CELL}>How often the magazine publishes</th>
                <th className={HEADER_CELL}>First issue arrives</th>
              </tr>
            </thead>
            <tbody>
              <tr className="even:bg-slate-50">
                <td className={CELL}>
                  <strong className="text-slate-900">Weekly</strong>
                </td>
                <td className={CELL}>4 to 6 weeks after ordering</td>
              </tr>
              <tr className="even:bg-slate-50">
                <td className={CELL}>
                  <strong className="text-slate-900">Bi-weekly and monthly</strong>
                </td>
                <td className={CELL}>6 to 9 weeks after ordering</td>
              </tr>
              <tr className="even:bg-slate-50">
                <td className={CELL}>
                  <strong className="text-slate-900">Bi-monthly and seasonal</strong>
                </td>
                <td className={CELL}>8 to 12 weeks after ordering</td>
              </tr>
              <tr className="even:bg-slate-50">
                <td className={CELL}>
                  <strong className="text-slate-900">Quarterly and less frequent</strong>
                </td>
                <td className={CELL}>10 to 12 weeks after ordering</td>
              </tr>
            </tbody>
          </Chart>

          <p>
            Many of our best-selling titles arrive sooner than this. Once the first issue lands, the
            rest follow on the publisher&apos;s normal schedule.
          </p>

          <h3>Why the first issue takes a while</h3>
          <p>
            Publishers print mailing labels weeks ahead of each issue to keep costs down. If your
            order lands after the cutoff for the issue currently in production, your subscription
            starts with the one after it. Occasionally a publisher will start you with the most
            recent issue instead so you are not waiting; if that issue is one you already have,{' '}
            <a href={contact}>let us know</a> and we will adjust your subscription.
          </p>

          <h3>Renewals and DiscountLock</h3>
          <p>
            The wait applies to new subscriptions only. Renewals, including subscriptions renewed
            automatically through <a href={buildUrl('/discountlock')}>DiscountLock</a>, are
            added to the end of your current term, so your issues keep coming without a gap.
          </p>

          <h3>How magazines are delivered</h3>
          <p>
            Publishers mail most magazines through the United States Postal Service, and a few use a
            courier. Because issues travel as standard mail, there is no tracking number for an
            individual magazine. Your DiscountMags account shows the estimated start date for each
            subscription.
          </p>

          <Note>
            <strong>Waiting longer than 12 weeks?</strong> <a href={contact}>Contact us</a> and we
            will check your subscription with the publisher and put it right.
          </Note>
        </Section>

        <Section id="digital">
          <h2>
            Digital magazines{' '}
            <Badge pill padding="snug" className="align-middle text-xs">
              Within 60 minutes
            </Badge>
          </h2>

          <p>
            Nothing ships. Digital subscriptions and single issues are delivered by our digital
            partners, Zinio and Magzter, and you should have access within 60 minutes of placing
            your order. You will get an activation email with instructions for the platform your
            magazine is on; if it has not shown up, check your spam or junk folder first.
          </p>
          <p>
            Zinio titles are also available in your{' '}
            <a href={buildUrl('/customer?xCmd=digitallibrary')}>DiscountMags digital library</a>, in
            the Zinio apps for iOS and Android, and at zinio.com. Magzter titles are available in
            the Magzter apps and at magzter.com.
          </p>
          <p>
            <a href={buildUrl('/zinio-unlimited')}>ZINIO Unlimited</a> and{' '}
            <a href={buildUrl('/magzter-gold')}>Magzter Gold</a> all-you-can-read plans start within
            60 minutes as well.
          </p>
          <p>
            Digital gifts go to the recipient&apos;s email address: enter it at the delivery step of
            checkout and they will receive the activation email while you receive the order
            confirmation.
          </p>
        </Section>

        <Section id="bundles">
          <h2>
            Print + Digital bundles{' '}
            <Badge pill padding="snug" className="align-middle text-xs">
              Free shipping
            </Badge>
          </h2>

          <p>
            A bundle is one subscription delivered two ways: the print edition in your mailbox and
            the digital edition on your device.
          </p>
          <p>
            Digital access is not instant with a bundle. The publisher sets up the digital side of
            your subscription along with the print side, so it takes longer than a digital-only
            order, but you will be reading online well before your first print issue arrives. How
            soon varies by publisher; you will receive an email with access instructions as soon as
            it is ready. Your first print issue then follows the same first-issue windows as a
            print-only subscription.
          </p>

          <Note>
            If you have been waiting more than a few weeks for digital access, or 12 weeks for your
            first print issue, <a href={contact}>contact us</a> and we will check with the
            publisher.
          </Note>
        </Section>

        <Section id="books">
          <h2>Books, calendars and other goods</h2>

          <h3>Shipping cost</h3>
          <p>
            Shipping for books, calendars and other physical goods is not free. It is calculated at
            checkout from a base rate plus a small charge for each additional item in the shipment.
          </p>

          <Chart>
            <thead>
              <tr>
                <th className={HEADER_CELL}>Method</th>
                <th className={HEADER_CELL}>Cost</th>
                <th className={HEADER_CELL}>Delivery after shipping</th>
              </tr>
            </thead>
            <tbody>
              <tr className="even:bg-slate-50">
                <td className={CELL}>
                  <strong className="text-slate-900">Standard</strong> (USPS Media Mail)
                </td>
                <td className={CELL}>$4.99 first item, $1.25 each additional item</td>
                <td className={CELL}>4 to 10 days</td>
              </tr>
              <tr className="even:bg-slate-50">
                <td className={CELL}>
                  <strong className="text-slate-900">Expedited</strong> (USPS)
                </td>
                <td className={CELL}>$7.81 first item, $1.00 each additional item</td>
                <td className={CELL}>3 to 6 days</td>
              </tr>
            </tbody>
          </Chart>

          <h3>Tracking</h3>
          <p>
            Orders usually ship within 2 to 3 business days. When yours does, we email you a
            tracking number and an estimated delivery date. Shipping charges do not apply to any
            magazine subscriptions in the same order; those still ship free.
          </p>

          <h3>Returns</h3>
          <p>
            Books and goods can be returned within 30 days of delivery. See the{' '}
            <a href={buildUrl('/refund-policy')}>Refund Policy</a> for the details.
          </p>
        </Section>

        <Section id="where">
          <h2>Where we ship</h2>

          <p>
            We deliver print subscriptions and physical goods anywhere in the United States,
            including Alaska, Hawaii, Puerto Rico and PO boxes. Digital products are available
            anywhere.
          </p>

          <h3>Military addresses (APO, FPO and DPO)</h3>
          <p>
            We are proud to ship to service members and their families overseas. Most magazines can
            be delivered to APO, FPO and DPO addresses; at checkout, choose AA, AE or AP as the
            state. Overseas military mail moves through the military postal system after it leaves
            the US, so allow a little extra time beyond the estimates above.
          </p>

          <h3>Gifts and multiple addresses</h3>
          <p>
            Every magazine in your order can go to a different address, so you can buy for yourself
            and a few gift recipients in one checkout. Gift subscriptions can include a themed
            e-card or postcard announcing the gift.
          </p>

          <h3>Correctional facilities</h3>
          <p>
            We accept orders for people in correctional facilities. Each facility sets its own rules
            about which magazines are allowed and may hold issues without notice, so check with the
            facility before ordering. Include the inmate ID number directly after the last name and
            use the name the facility has on file; mail without a matching name and number is
            usually not delivered.
          </p>
        </Section>

        {/* The closing offer of help, tinted rather than boxed so it reads as the end of
            the page rather than as a seventh section. Its own markup for the same reason
            /reviews' closing band is: ContentCard is the white panel, and this is not
            one. */}
        <section
          id={'help' satisfies SectionId}
          className={`flex flex-wrap items-center justify-between gap-4 rounded-lg border border-solid border-primary-200 bg-primary-50 p-5 md:p-8 ${SCROLL_OFFSET}`}
        >
          <div className="[&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
            <h2>Something not right?</h2>
            <p>
              Missed an issue, got a damaged copy or a duplicate, or moving and need to change your
              address? We will work with the publisher to sort it out.
            </p>
          </div>

          <ButtonLink href={contact}>Contact us</ButtonLink>
        </section>
      </div>
    </>
  );
}

ShippingDeliveryEstimates.layoutName = 'Layouts.Institutional';
